/**
 * Unified Component Configuration
 *
 * Central configuration for all PC component types.
 * Eliminates duplication across 8+ files and ensures consistency.
 */

import { ComponentType } from '../models/pc-build-model'
import type { ThemeColor } from '../types/theme'

export interface ComponentConfig {
  // Basic information
  name: string
  plural: string
  title: string

  // UI configuration
  themeColor: ThemeColor
  path: string
  icon: string

  // Model configuration
  required?: boolean
  multiple?: boolean
}

/**
 * Unified component configuration
 * Replaces all COMPONENT_CONFIG definitions across the codebase
 */
export const UNIFIED_COMPONENT_CONFIG: Record<ComponentType, ComponentConfig> = {
  [ComponentType.CPU]: {
    name: 'CPU',
    plural: 'CPUs',
    title: 'CPU Specifications',
    themeColor: 'blue',
    path: '/cpus',
    icon: 'cpu',
    required: true,
    multiple: false
  },

  [ComponentType.GPU]: {
    name: 'GPU',
    plural: 'GPUs',
    title: 'GPU Specifications',
    themeColor: 'purple',
    path: '/gpus',
    icon: 'gpu',
    required: false,
    multiple: true
  },

  [ComponentType.MEMORY]: {
    name: 'Memory',
    plural: 'Memory',
    title: 'Memory Specifications',
    themeColor: 'green',
    path: '/memory',
    icon: 'memory',
    required: true,
    multiple: true
  },

  [ComponentType.STORAGE]: {
    name: 'Storage',
    plural: 'Storage',
    title: 'Storage Specifications',
    themeColor: 'gray',
    path: '/storage',
    icon: 'storage',
    required: true,
    multiple: true
  },

  [ComponentType.PSU]: {
    name: 'PSU',
    plural: 'PSUs',
    title: 'PSU Specifications',
    themeColor: 'yellow',
    path: '/psus',
    icon: 'psu',
    required: true,
    multiple: false
  },

  [ComponentType.CASE]: {
    name: 'Case',
    plural: 'Cases',
    title: 'PC Case Specifications',
    themeColor: 'orange',
    path: '/cases',
    icon: 'case',
    required: true,
    multiple: false
  },

  [ComponentType.MOTHERBOARD]: {
    name: 'Motherboard',
    plural: 'Motherboards',
    title: 'Motherboard Specifications',
    themeColor: 'red',
    path: '/motherboards',
    icon: 'motherboard',
    required: true,
    multiple: false
  },

  [ComponentType.CPU_COOLER]: {
    name: 'CPU Cooler',
    plural: 'CPU Coolers',
    title: 'CPU Cooler Specifications',
    themeColor: 'teal',
    path: '/coolers',
    icon: 'cooler',
    required: true,
    multiple: false
  }
}

/**
 * Helper function to get component configuration
 */
export const getComponentConfig = (componentType: ComponentType): ComponentConfig => {
  return UNIFIED_COMPONENT_CONFIG[componentType]
}

/**
 * Helper function to get all component configurations
 */
export const getAllComponentConfigs = (): ComponentConfig[] => {
  return Object.values(UNIFIED_COMPONENT_CONFIG)
}

/**
 * Helper function to get component configuration by path
 */
export const getComponentConfigByPath = (path: string): ComponentConfig | undefined => {
  return Object.values(UNIFIED_COMPONENT_CONFIG).find(config => config.path === path)
}